import Component from '@glimmer/component'
import { service } from '@ember/service'
import type { IntlService } from 'ember-intl'
import type { RolloverCandidateStudents } from 'teacher-ui-client/rollover-student-import/utils'
import { t } from 'ember-intl'

interface Signature {
  Element: HTMLDivElement
  Args: {
    candidateStudents: RolloverCandidateStudents
  }
}

export class RolloverPreImportSummary extends Component<Signature> {
  @service intl!: IntlService

  private get studentCount() {
    return this.args.candidateStudents.length
  }

  /**
   * Determine created student count by counting candidate students without an existing student id.
   */
  get createdStudentCount() {
    return this.args.candidateStudents.filter((s) => !s.studentId).length
  }

  get updatedStudentCount() {
    return this.studentCount - this.createdStudentCount
  }

  /**
   * Determine the created class count by counting the candidate student's unique class names.
   */
  get createdClassCount() {
    return this.args.candidateStudents.reduce<string[]>((uniqueClassNames, candidateStudent) => {
      if (candidateStudent.schoolClassName && !uniqueClassNames.includes(candidateStudent.schoolClassName)) {
        return [...uniqueClassNames, candidateStudent.schoolClassName]
      }
      return uniqueClassNames
    }, []).length
  }

  get createdStudentCountLabel() {
    const resourceName = this.intl.t('manage.students.namePlural').toLowerCase()
    return this.intl.t('manage.rollover.steps.students.preImportSummary.createdCount', { resourceName })
  }

  get updatedStudentCountLabel() {
    const resourceName = this.intl.t('manage.students.namePlural').toLowerCase()
    return this.intl.t('manage.rollover.steps.students.preImportSummary.updatedCount', { resourceName })
  }

  get createdClassCountLabel() {
    const resourceName = this.intl.t('manage.schoolClasses.namePlural').toLowerCase()
    return this.intl.t('manage.rollover.steps.students.preImportSummary.createdCount', { resourceName })
  }

  <template>
    <div ...attributes>
      <span class="mb-4 block text-2xl font-semibold">
        {{t "manage.rollover.steps.students.preImportSummary.title"}}
      </span>
      <ul class="text-xl">
        <li data-test-updated-students>
          <span>{{this.updatedStudentCountLabel}}:</span>
          <span class="font-extrabold">{{this.updatedStudentCount}}</span>
        </li>
        <li data-test-created-students>
          <span>{{this.createdStudentCountLabel}}:</span>
          <span class="font-extrabold">{{this.createdStudentCount}}</span>
        </li>
        <li data-test-created-classes>
          <span>{{this.createdClassCountLabel}}:</span>
          <span class="font-extrabold">{{this.createdClassCount}}</span>
        </li>
      </ul>
    </div>
  </template>
}

export default RolloverPreImportSummary
